// Activity Feed — read-only activity_logs filtered by program (mirrors
// prototype's #page-docs layout). Heavy filtering already exists in the
// existing /activity-log page — we link out for that.
import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Loader2, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";
import { fetchJson, fmtDate, SectionHeader } from "./pccHelpers";

export default function PccActivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await fetchJson("/planning-command-center/activity", { limit: 150 });
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  async function loadProgram(pid) {
    setSelectedProgram(pid);
    try {
      const d = await fetchJson("/planning-command-center/activity", { program_id: pid, limit: 100 });
      setData(prev => ({ ...prev, activity: d.activity }));
    } catch {/* ignore */}
  }
  async function loadAll() {
    setSelectedProgram(null);
    try {
      const d = await fetchJson("/planning-command-center/activity", { limit: 150 });
      setData(d);
    } catch {/* ignore */}
  }

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="pcc-activity">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Activity Feed</h2>
          <p className="text-white/55 text-sm">Recent events across programs. Click a project on the left to filter.</p>
        </div>
        <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/[0.04]">
          <a href="/activity-log" target="_blank" rel="noreferrer">Full Activity Log <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></a>
        </Button>
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: "280px 1fr", height: "calc(100vh - 280px)", minHeight: 480 }}>
          <div className="border-r border-white/[0.08] bg-white/[0.02] overflow-y-auto">
            <div className="px-4 py-3 border-b border-white/[0.08] sticky top-0 bg-[#000000] flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Projects</div>
              <button onClick={loadAll} className={`text-[11px] ${!selectedProgram ? "text-amber-300" : "text-white/55"}`}>All</button>
            </div>
            <div className="divide-y divide-white/[0.06]">
              {(data.programs || []).map(p => (
                <button
                  key={p.id}
                  onClick={() => loadProgram(p.id)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-white/[0.03] ${selectedProgram === p.id ? "bg-amber-500/[0.08] border-l-2 border-l-amber-400" : ""}`}
                >
                  <div className="text-[13px] text-white truncate">{p.program_name}</div>
                  <div className="text-[10px] text-white/45 font-mono truncate">{p.client_name} · {p.stage}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto">
            <div className="px-6 py-3 border-b border-white/[0.08] sticky top-0 bg-[#000000]">
              <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">
                {selectedProgram ? data.programs?.find(p => p.id === selectedProgram)?.program_name : "All Activity"}
              </div>
            </div>
            <div className="divide-y divide-white/[0.06] px-6">
              {(data.activity || []).length === 0 ? (
                <div className="py-10 text-center text-white/40 text-sm">No activity logged.</div>
              ) : data.activity.map(a => (
                <div key={a.id || `${a.created_at}-${a.action}`} className="py-3">
                  <div className="text-[13px] text-white">{a.action || a.description}</div>
                  <div className="text-[11px] text-white/45 font-mono mt-0.5">{a.actor_name || a.user_email || "system"} · {fmtDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
